import { extractGroupAliases } from "./utils";

export function validateStage(stage, fields, pipeline = []) {
    // Base field names
    const fieldNames = fields.map(f => f.name).filter(Boolean);

    // Collect previous $group aliases from pipeline
    let previousGroupAliases = [];
    if (pipeline.length) {
        for (const st of pipeline) {
        if (st.id === stage.id) break; // stop at current stage
        if (st.type === "$group" && st.config?.aggregates) {
            previousGroupAliases.push(
            ...st.config.aggregates.map(a => a.as || a.alias).filter(Boolean)
            );
        }
        }
    }
  
    // Start with base object fields
    const allowedFields = [...fieldNames,...previousGroupAliases];
    console.log("Group Aliases:", allowedFields);
    const cfg = stage.config || {};
  
    // ----------------------------------------
    // Helper: get all previous group aliases (before current stage)
    // ----------------------------------------
    const getPreviousGroupAliases = () => {
      const aliases = new Set();
      for (const st of pipeline) {
        if (st === stage) break; // stop at current stage
        if (st.type === "$group" && Array.isArray(st.config?.aggregates)) {
          for (const ag of st.config.aggregates) {
            let alias = ag.alias?.trim();
            if (!alias && ag.field && ag.type) {
              alias = `${ag.type}_${ag.field}`;
            }
            if (alias) aliases.add(alias);
          }
        }
      }
      return aliases;
    };

    
    function getPreviousJoinProjectFields() {
      const fields = [];
    
      if (!pipeline?.length) return fields;
    
      for (const st of pipeline) {
        if (st.id === stage.id) break; // stop before current stage
        console.log("Checking stage for join fields:", st.config.lookupReturnType);
        if (
          st.type === "$join" &&
          Array.isArray(st.config?.fromFields)
        ) {
          // determine alias
          const joinAlias =
            st.config.lookupReturnType !== "merge" 
              ? null
              : st.config.as || st.config.from;
    
          for (const f of st.config.fromFields) {
            if (joinAlias) {
              // TimesheetEntry.Hour
              fields.push(`${joinAlias}.${f}`);
            } else {
              // merged fields (Hour)
              fields.push(f);
            }
          }
        }
      }
    
      return fields;
    }
    
    
  
    // ----------------------------------------
    // MATCH VALIDATION
    // ----------------------------------------
    if (stage.type === "$match") {
      if (!Array.isArray(cfg.rules) || cfg.rules.length === 0)
        return "Match stage requires at least one condition";
  
      const previousGroupAliases = getPreviousGroupAliases();
      const dynamicAllowedFields = new Set([...allowedFields, ...previousGroupAliases]);
  
      const validateRule = (rule, path = "") => {
        // Nested group rule
        if (rule.rules) {
          if (!Array.isArray(rule.rules) || rule.rules.length === 0)
            return `${path}Match group has no rules`;
  
          if (rule.mode && !["and", "or"].includes(rule.mode))
            return `${path}Invalid group operator: ${rule.mode}`;
  
          for (let i = 0; i < rule.rules.length; i++) {
            const err = validateRule(rule.rules[i], `${path}Rule ${i + 1}: `);
            if (err) return err;
          }
          return null;
        }
  
        // Simple rule
        if (!rule.field || rule.field.trim() === "")
          return `${path}Missing field`;
  
        if (!rule.operator || rule.operator.trim() === "")
          return `${path}Missing operator`;
  
        const op = rule.operator;
        const val = rule.value;
  
        // CUSTOM JSON
        if (op === "custom") {
          if (!rule.customJson || rule.customJson.trim() === "")
            return `${path}Custom JSON is empty`;
          try {
            JSON.parse(rule.customJson);
          } catch {
            return `${path}Custom JSON is invalid`;
          }
          return null;
        }
  
        // EXISTS
        if (op === "exists") {
          if (typeof val !== "boolean")
            return `${path}Exists operator requires true/false`;
          return null;
        }
  
        // BETWEEN
        if (op === "between") {
          if (!Array.isArray(val) || val.length !== 2)
            return `${path}Between requires an array of [min, max]`;
          if (val[0] == null || val[1] == null)
            return `${path}Between requires both min and max values`;
          return null;
        }
  
        // IN / NIN
        if (["in", "nin"].includes(op)) {
          if (!Array.isArray(val) || val.length === 0)
            return `${path}List operator requires at least one value`;
        }
  
        // All other operators
        if (val === undefined || val === null || val === "")
          return `${path}Missing value`;
  
        // FIELD VALIDATION — includes base fields + previous group aliases
        if (!dynamicAllowedFields.has(rule.field))
          return `${path}Unknown field: ${rule.field}`;
  
        return null;
      };
  
      for (let i = 0; i < cfg.rules.length; i++) {
        const err = validateRule(cfg.rules[i], `Condition ${i + 1}: `);
        if (err) return err;
      }
  
      if (cfg.mode && !["and", "or"].includes(cfg.mode))
        return `Invalid match root mode: ${cfg.mode}`;
    }
  
    // ----------------------------------------
    // GROUP VALIDATION
    // ----------------------------------------
    if (stage.type === "$group") {
        if (!Array.isArray(cfg.groupFields))
          return "Group requires groupFields[]";
    
        if (!Array.isArray(cfg.aggregates) || cfg.aggregates.length === 0)
          return "Group requires at least one aggregate";
    
        // All native fields
        const nativeFieldNames = fields.map(f => f.name);
        
        // Auto-import aliases from previous groups
        const prevAliases = extractGroupAliases(pipeline);
        const previousJoinProjectFields = getPreviousJoinProjectFields();
    
        // Final available fields
        const availableFields = [...nativeFieldNames, ...prevAliases, ...previousJoinProjectFields];
    
        // Validate group fields
        for (const gb of cfg.groupFields) {
          const fieldName = gb.field || gb; 
          if (!availableFields.includes(fieldName))
            return `Unknown groupBy field: ${fieldName}`;
        }
    
        // Validate aggregates
        for (const agg of cfg.aggregates) {
          if (!agg.type)
            return "Aggregate missing type";
    
          if (!agg.as)
            return "Aggregate missing alias (as)";
    
          if (agg.type !== "count") {
            if (!agg.field)
              return `Aggregate "${agg.as}" missing field`;
    
            if (!availableFields.includes(agg.field))
              return `Unknown aggregate field: ${agg.field}`;
          }
        }
    }
  
    // ----------------------------------------
    // PROJECT VALIDATION
    // ----------------------------------------
    if (stage.type === "$project") {
      if (!Array.isArray(cfg.include))
        return "Project requires include[]";
  
      const previousGroupAliases = getPreviousGroupAliases();
      const previousJoinProjectFields = getPreviousJoinProjectFields();
      console.log("Previous Join Project Fields:", previousJoinProjectFields);
      const dynamicAllowedFields = new Set([...allowedFields, ...previousGroupAliases, ...previousJoinProjectFields]);
  
      for (const f of cfg.include) {
        if (!dynamicAllowedFields.has(f))
          return `Unknown projected field: ${f}`;
      }
    }
  
    // ----------------------------------------
    // SORT VALIDATION
    // ----------------------------------------
    if (stage.type === "$sort") {
      if (!Array.isArray(cfg.sortBy) || cfg.sortBy.length === 0)
        return "Sort requires at least one sortBy entry";
  
      const previousGroupAliases = getPreviousGroupAliases();
      const dynamicAllowedFields = new Set([...allowedFields, ...previousGroupAliases]);
  
      for (const s of cfg.sortBy) {
        if (!s.field)
          return "Sort entry missing field";
        if (!dynamicAllowedFields.has(s.field))
          return `Unknown sort field: ${s.field}`;
        if (!["asc", "desc"].includes(s.order))
          return "Sort order must be 'asc' or 'desc'";
      }
    }
  
    // ----------------------------------------
    // LIMIT VALIDATION
    // ----------------------------------------
    if (stage.type === "$limit") {
      const { skip, limit } = cfg;
      if (skip === undefined || limit === undefined)
        return "Limit requires skip and limit numbers";
      if (isNaN(skip) || skip < 0)
        return "Skip must be a non-negative number";
      if (isNaN(limit) || limit < 0)
        return "Limit must be a non-negative number";
    }
  
    return null;
  }
  